import { ProfileAccountInfo } from "api/interfaces/accountInterface.interface";
import { LEVEL_TYPE } from "api/interfaces/commonInterface.interface";
import {
  CovaPermission,
  RecorderTypePermissionWithoutRole,
  SpectrumPermission,
  StrictUserPermissionKey,
  UserGroupRecorderTypePermission,
  UserGroupUserPermission,
  VmaxPermission,
} from "api/interfaces/userInterface.interface";

export type SpectrumRoleLevel = 0 | 1 | 2 | 3 | 4;
export type NVRRoleLevel = 10 | 11 | 99;
export type RecorderType = "spectrum" | "cova" | "vmax";

interface NVRPermission {
  vmax: VmaxPermission;
  cova: CovaPermission;
}

export const checkAutoApply = ({
  selectedAccount,
}: {
  selectedAccount: ProfileAccountInfo;
}): {
  autoApplyAccount: boolean;
  autoApplyRecorder: boolean;
} => {
  if (selectedAccount.accountLevel === LEVEL_TYPE.EU) {
    return {
      autoApplyAccount: false,
      autoApplyRecorder: !selectedAccount.isAdminGroup ? false : true,
    };
  }
  return {
    autoApplyAccount: !selectedAccount.isAdminGroup ? false : true,
    autoApplyRecorder: false,
  };
};

// admin group 에 속한 user 는 user management 를 제어할 수 있음.
export const checkUserPermission = (isAdminGroup: boolean) => {
  if (isAdminGroup) {
    return {
      checked: {
        enableAllPermissions: true,
        manageUsersAndGroups: true,
        viewUsersAndGroups: true,
      },
      disable: {
        enableAllPermissions: false,
        manageUsersAndGroups: false,
        viewUsersAndGroups: false,
      },
    };
  }

  return {
    checked: {
      enableAllPermissions: false,
      manageUsersAndGroups: false,
      viewUsersAndGroups: false,
    },
    disable: {
      enableAllPermissions: true,
      manageUsersAndGroups: true,
      viewUsersAndGroups: true,
    },
  };
};

export const defaultGroupName = ["administrator", "manager", "viewer"];
export const allSubcriptionPermission: StrictUserPermissionKey[] = [
  "subscriptionOrganization",
  "subscriptionKeys",
  "serviceAndPricing",
  "cumulus",
  "manageBillingAndLicenses",
];

export const allDisabledUserPermission: UserGroupUserPermission = {
  enableAllPermissions: true,
  manageAlertRules: true,
  viewAlerts: true,
  manageUsersAndGroups: true,
  viewUsersAndGroups: true,
  manageReportRules: true,
  viewReports: true,
  manageRecordersAndGroups: true,
  viewRecordersAndGroups: true,
  manageAccount: true,
  viewAccount: true,
  view: true,
  manageBillingAndLicenses: true,
  enableDelete: true,
  subscriptionOrganization: true,
  suspend: true,
  lowLevelAccess: true,
  subscriptionKeys: true,
  generateKeys: true,
  serviceAndPricing: true,
  serviceAndPricingUpdate: true,
  cumulus: true,
  generateCumulus: true,
  myOrganization: true,
  videoGuideSetting: true,
  techSupport: true,
  siteNotice: true,
  emails: true,
  viewSubscription: true,
  manageSubscription: true,
  announcement: true,
  logging: true,
  releaseNotes: true,
  manageMaintenance: true,
};

export const defaultCheckedUserPermission = (
  isAdminGroup: boolean
): Record<LEVEL_TYPE, UserGroupUserPermission> => {
  return {
    [LEVEL_TYPE.DW]: {
      manageAlertRules: false,
      viewAlerts: false,
      manageReportRules: false,
      viewReports: false,
      manageRecordersAndGroups: false,
      viewRecordersAndGroups: false,
      manageAccount: true,
      viewAccount: true,
      view: false,
      manageBillingAndLicenses: false,
      groupName: "",
      enableDelete: true,
      subscriptionOrganization: true,
      suspend: true,
      lowLevelAccess: true,
      subscriptionKeys: true,
      generateKeys: true,
      serviceAndPricing: true,
      serviceAndPricingUpdate: true,
      cumulus: true,
      generateCumulus: true,
      myOrganization: true,
      videoGuideSetting: true,
      techSupport: true,
      siteNotice: true,
      emails: true,
      viewSubscription: false,
      manageSubscription: false,
      announcement: true,
      logging: true,
      releaseNotes: true,
      manageMaintenance: true,
      ...checkUserPermission(isAdminGroup).checked,
    },
    [LEVEL_TYPE.SP]: {
      manageAlertRules: true,
      viewAlerts: true,
      manageReportRules: true,
      viewReports: true,
      manageRecordersAndGroups: false,
      viewRecordersAndGroups: false,
      manageAccount: true,
      viewAccount: true,
      view: false,
      manageBillingAndLicenses: false,
      enableDelete: true,
      subscriptionOrganization: false,
      suspend: false,
      lowLevelAccess: false,
      subscriptionKeys: false,
      generateKeys: false,
      serviceAndPricing: false,
      serviceAndPricingUpdate: false,
      cumulus: false,
      generateCumulus: false,
      myOrganization: false,
      videoGuideSetting: false,
      techSupport: false,
      siteNotice: false,
      emails: false,
      viewSubscription: false,
      manageSubscription: false,
      announcement: false,
      logging: false,
      releaseNotes: false,
      manageMaintenance: true,
      groupName: "",
      ...checkUserPermission(isAdminGroup).checked,
    },
    [LEVEL_TYPE.CP]: {
      manageAlertRules: true,
      viewAlerts: true,
      manageReportRules: true,
      viewReports: true,
      manageRecordersAndGroups: false,
      viewRecordersAndGroups: false,
      manageAccount: true,
      viewAccount: true,
      view: false,
      manageBillingAndLicenses: true,
      enableDelete: true,
      subscriptionOrganization: false,
      suspend: false,
      lowLevelAccess: false,
      subscriptionKeys: false,
      generateKeys: false,
      serviceAndPricing: false,
      serviceAndPricingUpdate: false,
      cumulus: false,
      generateCumulus: false,
      myOrganization: false,
      videoGuideSetting: false,
      techSupport: false,
      siteNotice: false,
      emails: false,
      viewSubscription: false,
      manageSubscription: false,
      announcement: false,
      logging: false,
      releaseNotes: false,
      manageMaintenance: true,
      groupName: "",
      ...checkUserPermission(isAdminGroup).checked,
    },
    [LEVEL_TYPE.EU]: {
      manageAlertRules: true,
      viewAlerts: true,
      manageReportRules: true,
      viewReports: true,
      manageRecordersAndGroups: true,
      viewRecordersAndGroups: true,
      manageAccount: false,
      viewAccount: false,
      view: true,
      manageBillingAndLicenses: false,
      enableDelete: true,
      subscriptionOrganization: false,
      suspend: false,
      lowLevelAccess: false,
      subscriptionKeys: false,
      generateKeys: false,
      serviceAndPricing: false,
      serviceAndPricingUpdate: false,
      cumulus: false,
      generateCumulus: false,
      myOrganization: false,
      videoGuideSetting: false,
      techSupport: false,
      siteNotice: false,
      emails: false,
      viewSubscription: true,
      manageSubscription: true,
      announcement: false,
      logging: false,
      releaseNotes: false,
      manageMaintenance: true,
      groupName: "",
      ...checkUserPermission(isAdminGroup).checked,
    },
    [LEVEL_TYPE.INTL]: {
      manageAlertRules: true,
      viewAlerts: true,
      manageReportRules: true,
      viewReports: true,
      manageRecordersAndGroups: true,
      viewRecordersAndGroups: true,
      manageAccount: true,
      viewAccount: true,
      view: true,
      manageBillingAndLicenses: true,
      enableDelete: true,
      subscriptionOrganization: true,
      suspend: true,
      lowLevelAccess: true,
      subscriptionKeys: true,
      generateKeys: true,
      serviceAndPricing: true,
      serviceAndPricingUpdate: true,
      cumulus: true,
      generateCumulus: true,
      myOrganization: true,
      videoGuideSetting: true,
      techSupport: true,
      siteNotice: true,
      emails: true,
      viewSubscription: true,
      manageSubscription: true,
      announcement: true,
      logging: true,
      releaseNotes: true,
      manageMaintenance: true,
      groupName: "",
      ...checkUserPermission(isAdminGroup).checked,
    },
  };
};

/**
 * Recorder Permission -------------------------
 */
export const allDisabledRecorderPermission: RecorderTypePermissionWithoutRole =
  {
    spectrum: {
      cameraSetting: true,
      viewArchive: true,
      exportArchive: true,
      viewBookmark: true,
      modifyBookmark: true,
      viewLive: true,
      cameraUserInput: true,
    },
    vmax: {
      system: true,
      alarm: true,
      shutdown: true,
      playback: true,
      search: true,
      device: true,
      export: true,
      ptz: true,
      backup: true,
      network: true,
      record: true,
    },
    cova: {
      localCamera: true,
      remoteCamera: true,
      disk: true,
      aiEvent: true,
      schedule: true,
      localSystem: true,
      remoteSystem: true,
      accountAuthority: true,
      remoteLogin: true,
      network: true,
      record: true,
      talkback: true,
    },
  };

export const adminRecorderPermission: NVRPermission = {
  vmax: {
    system: true,
    alarm: true,
    shutdown: true,
    playback: true,
    search: true,
    device: true,
    export: true,
    ptz: true,
    backup: true,
    network: true,
    record: true,
  },
  cova: {
    localCamera: true,
    remoteCamera: true,
    disk: true,
    aiEvent: true,
    schedule: true,
    localSystem: true,
    remoteSystem: true,
    accountAuthority: true,
    remoteLogin: true,
    network: true,
    record: true,
    talkback: true,
  },
};

export const viewerRecorderPermission: NVRPermission = {
  vmax: {
    system: false,
    alarm: false,
    shutdown: false,
    playback: true,
    search: true,
    device: false,
    export: true,
    ptz: false,
    backup: true,
    network: true,
    record: false,
  },
  cova: {
    localCamera: false,
    remoteCamera: false,
    disk: false,
    aiEvent: false,
    schedule: false,
    localSystem: true,
    remoteSystem: true,
    accountAuthority: false,
    remoteLogin: true,
    network: true,
    record: true,
    talkback: true,
  },
};

export const customRecorderPermission: NVRPermission = {
  vmax: {
    system: false,
    alarm: false,
    shutdown: false,
    playback: true,
    search: true,
    device: false,
    export: false,
    ptz: false,
    backup: false,
    network: true,
    record: false,
  },
  cova: {
    localCamera: false,
    remoteCamera: false,
    disk: false,
    aiEvent: false,
    schedule: false,
    localSystem: false,
    remoteSystem: false,
    accountAuthority: false,
    remoteLogin: true,
    network: true,
    record: false,
    talkback: true,
  },
};

export const customDisabledRecorderPermission: RecorderTypePermissionWithoutRole =
  {
    spectrum: {
      cameraSetting: true,
      viewArchive: true,
      exportArchive: true,
      viewBookmark: true,
      modifyBookmark: true,
      viewLive: false,
      cameraUserInput: true,
    },
    vmax: {
      system: false,
      alarm: false,
      shutdown: false,
      playback: false,
      search: true,
      device: false,
      export: false,
      ptz: false,
      backup: false,
      network: true,
      record: false,
    },
    cova: {
      localCamera: false,
      remoteCamera: false,
      disk: true,
      aiEvent: false,
      schedule: false,
      localSystem: false,
      remoteSystem: false,
      accountAuthority: true,
      remoteLogin: false,
      network: true,
      record: false,
      talkback: false,
    },
  };

export const defaultSpectrumRoleCheck: Record<
  SpectrumRoleLevel,
  SpectrumPermission
> = {
  // Power Users
  0: {
    cameraSetting: true,
    viewArchive: true,
    exportArchive: true,
    viewBookmark: true,
    modifyBookmark: true,
    viewLive: true,
    cameraUserInput: true,
  },

  // Advanced Viewers
  1: {
    cameraSetting: false,
    viewArchive: true,
    exportArchive: true,
    viewBookmark: true,
    modifyBookmark: true,
    viewLive: true,
    cameraUserInput: true,
  },

  // Viewers
  2: {
    cameraSetting: false,
    viewArchive: true,
    exportArchive: true,
    viewBookmark: true,
    modifyBookmark: false,
    viewLive: true,
    cameraUserInput: false,
  },

  // Live Viewers
  3: {
    cameraSetting: false,
    viewArchive: false,
    exportArchive: false,
    viewBookmark: false,
    modifyBookmark: false,
    viewLive: true,
    cameraUserInput: false,
  },

  // System Health Viewers
  4: {
    cameraSetting: false,
    viewArchive: false,
    exportArchive: false,
    viewBookmark: false,
    modifyBookmark: false,
    viewLive: true,
    cameraUserInput: false,
  },

  // // Custom
  // 99: {
  //   cameraSetting: false,
  //   viewArchive: false,
  //   exportArchive: false,
  //   viewBookmark: false,
  //   modifyBookmark: false,
  //   viewLive: false,
  //   cameraUserInput: false,
  // },
};

export const formatLevelCheckList = ({
  type,
  role,
  checkType,
}: {
  type: RecorderType;
  role: SpectrumRoleLevel | NVRRoleLevel;
  checkType: "disable" | "check";
}) => {
  if (type === "spectrum") {
    if (checkType === "check") {
      return {
        ...defaultSpectrumRoleCheck[role as SpectrumRoleLevel],
        role: role as SpectrumRoleLevel,
      };
    }
    if (checkType === "disable") {
      return {
        ...allDisabledRecorderPermission[type],
      };
    }
  } else {
    if (checkType === "check") {
      if (role === 10) {
        return {
          ...adminRecorderPermission[type],
          role: role as NVRRoleLevel,
        };
      }
      if (role === 11) {
        return {
          ...viewerRecorderPermission[type],
          role: role as NVRRoleLevel,
        };
      }
      return {
        ...customRecorderPermission[type],
        role: role as NVRRoleLevel,
      };
    }
    if (checkType === "disable") {
      if (role === 99) {
        return {
          ...customDisabledRecorderPermission[type],
        };
      }
      return {
        ...allDisabledRecorderPermission[type],
      };
    }
  }
};

export const allFormatLevel = ({
  role = "custom",
  checkType,
}: {
  role?: "admin" | "custom";
  checkType: "disable" | "check";
}): UserGroupRecorderTypePermission => {
  const nvrRole = role === "custom" ? 99 : 10;
  const spectrumRole = 0;

  return {
    spectrum: {
      ...formatLevelCheckList({
        type: "spectrum",
        role: spectrumRole,
        checkType,
      }),
    } as { role: SpectrumRoleLevel } & SpectrumPermission,
    vmax: {
      ...formatLevelCheckList({ type: "vmax", role: nvrRole, checkType }),
    } as { role: NVRRoleLevel } & VmaxPermission,
    cova: {
      ...formatLevelCheckList({ type: "cova", role: nvrRole, checkType }),
    } as { role: NVRRoleLevel } & CovaPermission,
  };
};

export const getSpectrumCustomDisableList = (
  checkedSpectrum?: SpectrumPermission
): SpectrumPermission => {
  if (!checkedSpectrum) {
    return {
      ...customDisabledRecorderPermission.spectrum,
    };
  }
  const { viewLive, viewArchive, viewBookmark, cameraUserInput } =
    checkedSpectrum;

  const isViewArchiveDisabled = !viewLive;
  const isCameraUserInputDisabled = !viewLive;

  const isViewBookmarkDisabled = !viewLive || !viewArchive;
  const isExportArchiveDisabled = !viewLive || !viewArchive;

  const isModifyBookmarkDisabled = !viewBookmark;
  const isCameraSettingDisabled = !cameraUserInput;

  return {
    viewLive: false,
    viewArchive: isViewArchiveDisabled,
    cameraUserInput: isCameraUserInputDisabled,
    viewBookmark: isViewBookmarkDisabled,
    exportArchive: isExportArchiveDisabled,
    modifyBookmark: isModifyBookmarkDisabled,
    cameraSetting: isCameraSettingDisabled,
  };
};

export const getSpectrumDisableList = (
  spectrum: { role: SpectrumRoleLevel } & SpectrumPermission
): SpectrumPermission => {
  // if (spectrum.role === 99) {
  //   const { role, ...rest } = spectrum;
  //   return getSpectrumCustomDisableList(rest);
  // }

  // 기본/관리자/뷰어 등 나머지는 기존 formatLevelCheckList 로 처리
  return formatLevelCheckList({
    type: "spectrum",
    role: spectrum.role,
    checkType: "disable",
  }) as SpectrumPermission;
};
const getNvrDisableList = <T extends "vmax" | "cova">(
  type: T,
  permission: { role: NVRRoleLevel }
): T extends "vmax" ? VmaxPermission : CovaPermission => {
  return formatLevelCheckList({
    type,
    role: permission.role,
    checkType: "disable",
  }) as any;
};

export const currentRecorderPermissionDisableList = (
  permission?: UserGroupRecorderTypePermission
): RecorderTypePermissionWithoutRole => {
  if (!permission) {
    return { ...customDisabledRecorderPermission };
  }
  return {
    spectrum: getSpectrumDisableList(permission.spectrum),
    vmax: getNvrDisableList("vmax", permission.vmax),
    cova: getNvrDisableList("cova", permission.cova),
  };
};
